import { Schema } from "effect"
import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "effect/http-api"
import { Authentication } from "./AuthApi.ts"
import {
  InvalidDeclaration,
  LibraryUnavailable,
  PushFailed,
  ReadOnlyLibrary,
  SourceTooLarge,
  StrategyCompileError,
  StrategyNotFound,
  StrategyVersionConflict
} from "./Errors.ts"
import { EpochSeconds, StrategyId, StrategyRef } from "./Money.ts"

// User-written strategies (docs/design/0005, P4). Versions are immutable: source
// plus the JS compiled from it, which the browser backtest and the server paper
// run both execute. A runner pins "user:<id>@<version>".

/** Source limit, in UTF-8 bytes (SourceTooLarge beyond it). */
export const MAX_STRATEGY_SOURCE_BYTES = 256 * 1024
/** Declaration limit, in bytes of its JSON (InvalidDeclaration beyond it). */
export const MAX_STRATEGY_DECLARATION_BYTES = 64 * 1024

/** "user:<strategyId>@<version>": a user strategy pinned to one version (runners, the tester). */
export const userStrategyRef = (strategyId: StrategyId, version: number): StrategyRef =>
  StrategyRef.make(`user:${strategyId}@${version}`)

/** A user strategy ref's parts: `version` is null for "user:<id>" (follows the latest). Null for any other ref. */
export const parseUserStrategyRef = (ref: string): { readonly strategyId: StrategyId; readonly version: number | null } | null => {
  const match = /^user:([A-Za-z0-9_-]{1,64})(?:@([1-9]\d{0,8}))?$/.exec(ref)
  if (match === null) return null
  return { strategyId: StrategyId.make(match[1]!), version: match[2] === undefined ? null : Number(match[2]) }
}

export const StrategyName = Schema.String.pipe(
  Schema.check(Schema.isTrimmed(), Schema.isMinLength(1), Schema.isMaxLength(80))
)

/** One input as the strategy declares it (the shape the settings form edits). */
export const StrategyInputDeclaration = Schema.Struct({
  id: Schema.String,
  type: Schema.String,
  title: Schema.optional(Schema.String),
  defval: Schema.Json,
  // Finite only: JSON 1e999 would decode as Infinity, be stored as null, and fail to read back.
  min: Schema.optional(Schema.Finite),
  max: Schema.optional(Schema.Finite),
  step: Schema.optional(Schema.Finite),
  options: Schema.optional(Schema.Array(Schema.String)),
  group: Schema.optional(Schema.String),
  tooltip: Schema.optional(Schema.String)
})
export type StrategyInputDeclaration = typeof StrategyInputDeclaration.Type

/**
 * What the browser found when it ran the draft: title, inputs and strategy()
 * properties. Untrusted metadata, used only for UI defaults; the server never
 * runs user code to check it before P5.
 */
export const StrategyDeclaration = Schema.Struct({
  title: Schema.String,
  inputs: Schema.Array(StrategyInputDeclaration),
  properties: Schema.JsonObject
})
export type StrategyDeclaration = typeof StrategyDeclaration.Type

export const StrategySummary = Schema.Struct({
  id: StrategyId,
  name: Schema.String,
  latestVersion: Schema.Int,
  createdAt: EpochSeconds,
  /** When the latest version was saved. */
  updatedAt: EpochSeconds,
  archivedAt: Schema.NullOr(EpochSeconds),
  /** A file in the strategy library (`strategies/<id>.ts`, docs/design/0012), shared by every user. */
  library: Schema.Boolean
})
export type StrategySummary = typeof StrategySummary.Type

export const StrategyVersionInfo = Schema.Struct({
  version: Schema.Int,
  sourceSha256: Schema.String,
  createdAt: EpochSeconds
})
export type StrategyVersionInfo = typeof StrategyVersionInfo.Type

/** One immutable version. */
export const StrategyVersion = Schema.Struct({
  strategyId: StrategyId,
  version: Schema.Int,
  source: Schema.String,
  /** The JS that runs (sucrase output), in the browser and on the server alike. */
  compiled: Schema.String,
  /** e.g. "sucrase@3.35.0". */
  compiler: Schema.String,
  sourceSha256: Schema.String,
  declaration: StrategyDeclaration,
  createdAt: EpochSeconds
})
export type StrategyVersion = typeof StrategyVersion.Type

export const StrategyDetail = Schema.Struct({
  strategy: StrategySummary,
  /** Oldest first. */
  versions: Schema.Array(StrategyVersionInfo),
  latest: StrategyVersion
})
export type StrategyDetail = typeof StrategyDetail.Type

export const NewStrategy = Schema.Struct({
  name: StrategyName,
  source: Schema.String,
  declaration: StrategyDeclaration
})
export type NewStrategy = typeof NewStrategy.Type

export const NewStrategyVersion = Schema.Struct({
  /** The version the edit started from; StrategyVersionConflict when it is no longer the latest. */
  baseVersion: Schema.Int,
  source: Schema.String,
  declaration: StrategyDeclaration
})
export type NewStrategyVersion = typeof NewStrategyVersion.Type

export const UpdateStrategy = Schema.Struct({ name: StrategyName })
export type UpdateStrategy = typeof UpdateStrategy.Type

// --- The library: strategy files in git (docs/design/0012) ----------------------------------

export const LibraryChange = Schema.Struct({
  /** Relative to the repository, e.g. "strategies/ema-cross.ts". */
  path: Schema.String,
  status: Schema.Literals(["added", "modified", "deleted"])
})
export type LibraryChange = typeof LibraryChange.Type

export const StrategyLibraryStatus = Schema.Struct({
  /** False on prod: the release's tested strategies, nothing saved or pushed. */
  editable: Schema.Boolean,
  /** The branch pushes go to; null without git. */
  branch: Schema.NullOr(Schema.String),
  /** Whether a GITHUB_TOKEN is configured to push with. */
  canPush: Schema.Boolean,
  /** Strategy files changed and not committed yet. */
  changes: Schema.Array(LibraryChange),
  /** Commits made but not pushed (an earlier push failed). */
  unpushedCommits: Schema.Int
})
export type StrategyLibraryStatus = typeof StrategyLibraryStatus.Type

export const PushResult = Schema.Struct({
  /** The commit pushed; null when there was nothing to push. */
  commit: Schema.NullOr(Schema.String),
  branch: Schema.String,
  /** Strategy files in the pushed commit(s). */
  files: Schema.Int
})
export type PushResult = typeof PushResult.Type

/** What a sync of the files into versions did. */
export const LibrarySync = Schema.Struct({
  created: Schema.Array(StrategyId),
  updated: Schema.Array(StrategyId),
  archived: Schema.Array(StrategyId),
  /** Files that couldn't become a version (they don't compile), with why. */
  failed: Schema.Array(Schema.Struct({ id: Schema.String, message: Schema.String }))
})
export type LibrarySync = typeof LibrarySync.Type

const strategyParams = { id: StrategyId }

export class StrategiesApi extends HttpApiGroup.make("strategies")
  .add(
    // Before "/:id", which would match "library" too.
    HttpApiEndpoint.get("library", "/library", { success: StrategyLibraryStatus, error: LibraryUnavailable }),
    /** Commits the changed strategy files and pushes them to the library's branch. */
    HttpApiEndpoint.post("push", "/library/push", { success: PushResult, error: [ReadOnlyLibrary, PushFailed, LibraryUnavailable] }),
    /** Reads the files again: changed ones become new versions (e.g. after a git pull). */
    HttpApiEndpoint.post("sync", "/library/sync", { success: LibrarySync, error: LibraryUnavailable })
  )
  .add(
    /** The caller's strategies, archived ones included. */
    HttpApiEndpoint.get("list", "/", { success: Schema.Array(StrategySummary) }),
    /** Compiles the source and saves it as version 1. */
    HttpApiEndpoint.post("create", "/", {
      payload: NewStrategy,
      success: StrategyDetail.pipe(HttpApiSchema.status(201)),
      error: [StrategyCompileError, SourceTooLarge, InvalidDeclaration, ReadOnlyLibrary, LibraryUnavailable]
    }),
    HttpApiEndpoint.get("get", "/:id", {
      params: strategyParams,
      success: StrategyDetail,
      error: StrategyNotFound
    }),
    HttpApiEndpoint.get("version", "/:id/versions/:version", {
      params: { id: StrategyId, version: Schema.Int },
      success: StrategyVersion,
      error: StrategyNotFound
    }),
    HttpApiEndpoint.post("appendVersion", "/:id/versions", {
      params: strategyParams,
      payload: NewStrategyVersion,
      success: StrategyVersion.pipe(HttpApiSchema.status(201)),
      error: [StrategyNotFound, StrategyCompileError, StrategyVersionConflict, SourceTooLarge, InvalidDeclaration, ReadOnlyLibrary, LibraryUnavailable]
    }),
    HttpApiEndpoint.patch("update", "/:id", {
      params: strategyParams,
      payload: UpdateStrategy,
      success: StrategySummary,
      error: [StrategyNotFound, ReadOnlyLibrary]
    }),
    HttpApiEndpoint.post("archive", "/:id/archive", {
      params: strategyParams,
      success: StrategySummary,
      error: [StrategyNotFound, ReadOnlyLibrary, LibraryUnavailable]
    })
  )
  .middleware(Authentication)
  .prefix("/strategies")
{}
